import React, { useContext } from 'react'
import { TestStatusTabs } from '@/api/Clients/Types/AppropriatenessTestDto'
import { PermissionDto } from '@/api/Identity/Types/PermissionDto'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { DividendsTab } from '@/routes/Brokerage/Dividends/DividendsNavigation'
import { TicketsViewType } from '@/routes/Tickets/TicketsViewType'
import { useAppSelector } from '@/store'
import { AuthSessionContext } from '@/utils/AuthContext'
import { appTestRouteUrl, isFeatureEnabled, links } from '@/utils/featureToggles'
import { hasPermission, hasSomePermissions } from '@/utils/permissionsValidator'
import { TenantFeature } from '@/utils/Tenant'
import { calculateTotalAmountOfTickets } from '@/utils/ticket.utils'
import {
  Activity,
  ArrowLeftRight,
  Award,
  BarChart,
  Bell,
  Blocks,
  Bot,
  Briefcase,
  Building,
  ChartColumn,
  CirclePercent,
  Code,
  CreditCard,
  DollarSign,
  FileCheck,
  FileCheckCorner,
  FileText,
  FileUp,
  Flag,
  Gift,
  Globe,
  Image,
  Landmark,
  Layers,
  LayoutDashboard,
  Link2,
  Mail,
  MapPin,
  Megaphone,
  Percent,
  PlusCircle,
  RefreshCw,
  Repeat,
  Server,
  Settings,
  ShieldCheck,
  Target,
  Ticket,
  TrendingUp,
  Trophy,
  Upload,
  User,
  UserCog,
  UserPlus,
  Users,
  Users2,
  Wallet,
  Wrench,
} from 'lucide-react'

export interface NavItem {
  title: string
  href?: string
  icon: React.ElementType
  badge?: number
  permission?: PermissionsEnum
  permissions?: PermissionsEnum[]
  feature?: TenantFeature
  checkPermission?: () => boolean
  children?: NavItem[]
}

export const useNavItems = (): NavItem[] => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const hasTradingAccountsItems =
    (isFeatureEnabled(TenantFeature.recordsTradingAccounts) &&
      hasPermission(permissions, PermissionsEnum.CanBrowseTradingAccounts)) ||
    hasPermission(permissions, PermissionsEnum.CanBrowseDemoAccounts) ||
    hasPermission(permissions, PermissionsEnum.CanBrowseWelcomeAccounts)

  const outstandingTickets = useAppSelector((state) => state.tickets.outstandingTickets)
  const myTicketsCount = calculateTotalAmountOfTickets(outstandingTickets)

  const navItems: NavItem[] = [
    {
      title: 'Dashboard',
      href: links.dashboard.url,
      icon: LayoutDashboard,
      feature: TenantFeature.dashboard,
      permission: PermissionsEnum.CanViewPAMDashboard,
    },
    {
      title: 'Records',
      icon: Users,
      checkPermission: () =>
        (isFeatureEnabled(TenantFeature.recordClients) &&
          hasPermission(permissions, PermissionsEnum.CanBrowseClients)) ||
        (isFeatureEnabled(TenantFeature.recordsWallets) &&
          hasPermission(permissions, PermissionsEnum.CanBrowseWallets)) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseTradingAccounts) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseDemoAccounts) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseWelcomeAccounts) ||
        (isFeatureEnabled(TenantFeature.recordsAccountGroups) &&
          hasPermission(permissions, PermissionsEnum.CanBrowseAccountGroups)) ||
        (isFeatureEnabled(TenantFeature.recordsIntroducingBrokers) &&
          hasPermission(permissions, PermissionsEnum.CanBrowseIntroducingBrokers)) ||
        (isFeatureEnabled(TenantFeature.recordLeads) &&
          hasPermission(permissions, PermissionsEnum.CanViewLeads)) ||
        (isFeatureEnabled(TenantFeature.recordsExchangeRates) &&
          hasPermission(permissions, PermissionsEnum.CanBrowseExchangeRates)),
      children: [
        {
          title: 'Clients',
          href: links.recordsClients.url,
          icon: Users,
          permission: PermissionsEnum.CanBrowseClients,
          feature: TenantFeature.recordClients,
        },
        {
          title: 'Wallets',
          href: links.recordsWallets.url,
          icon: Wallet,
          permission: PermissionsEnum.CanBrowseWallets,
          feature: TenantFeature.recordsWallets,
        },
        ...(hasTradingAccountsItems
          ? [
              {
                title: 'Trading Accounts',
                icon: CreditCard,
                checkPermission: () => hasTradingAccountsItems,
                children: [
                  {
                    title: 'Live',
                    href: links.recordsTradingAccounts.url,
                    icon: CreditCard,
                    permission: PermissionsEnum.CanBrowseTradingAccounts,
                    feature: TenantFeature.recordsTradingAccounts,
                  },
                  {
                    title: 'Demo',
                    href: links.recordsDemoAccounts.url,
                    icon: CreditCard,
                    permission: PermissionsEnum.CanBrowseDemoAccounts,
                    feature: TenantFeature.recordsDemoAccounts,
                  },
                  {
                    title: 'Welcome',
                    href: links.recordsWelcomeAccounts.url,
                    icon: CreditCard,
                    permission: PermissionsEnum.CanBrowseWelcomeAccounts,
                    feature: TenantFeature.recordsWelcomeAccounts,
                  },
                ],
              },
            ]
          : []),
        {
          title: 'Account Groups',
          href: links.recordsAccountGroups.url,
          icon: Layers,
          permission: PermissionsEnum.CanBrowseAccountGroups,
          feature: TenantFeature.recordsAccountGroups,
        },
        {
          title: 'Introducing Brokers',
          href: links.recordsIntroducingBrokers.url,
          icon: UserPlus,
          permission: PermissionsEnum.CanBrowseIntroducingBrokers,
          feature: TenantFeature.recordsIntroducingBrokers,
        },
        {
          title: 'Affiliates',
          href: links.recordsAffiliates.url,
          icon: Award,
          permission: PermissionsEnum.CanBrowseAffiliates,
          feature: TenantFeature.recordsAffiliates,
        },
        {
          title: 'Payment Agents',
          href: links.recordsPaymentAgents.url,
          icon: Briefcase,
          permission: PermissionsEnum.CanBrowsePaymentAgents,
          feature: TenantFeature.recordsPaymentAgents,
        },
        {
          title: 'Leads',
          href: links.recordsLeads.url,
          icon: Target,
          permission: PermissionsEnum.CanViewLeads,
          feature: TenantFeature.recordLeads,
        },
        {
          title: 'Exchange Rates',
          href: links.recordsExchangeRates.url,
          icon: RefreshCw,
          permission: PermissionsEnum.CanBrowseExchangeRates,
          feature: TenantFeature.recordsExchangeRates,
        },
        {
          title: 'Currency Markups',
          href: links.recordsCurrencyMarkups.url,
          icon: Percent,
          permission: PermissionsEnum.CanEditMarkup,
          feature: TenantFeature.recordsCurrencyMarkups,
        },
      ],
    },
    {
      title: 'Tickets',
      icon: Ticket,
      badge: myTicketsCount,
      checkPermission: () =>
        hasPermission(permissions, PermissionsEnum.CanBrowsePaymentsTickets) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseBackofficeTickets) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseTranslationsTickets) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseBrokerageTickets) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseComplianceTickets) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseSalesTickets) ||
        hasPermission(permissions, PermissionsEnum.CanViewTicket),
      children: [
        {
          title: 'Payments',
          href: links.ticketsPayments.url,
          icon: DollarSign,
          badge: outstandingTickets[TicketsViewType.Payments] || 0,
          permission: PermissionsEnum.CanBrowsePaymentsTickets,
          feature: TenantFeature.ticketsPayments,
        },
        {
          title: 'Backoffice',
          href: links.ticketsBackoffice.url,
          icon: Briefcase,
          badge: outstandingTickets[TicketsViewType.BackOffice] || 0,
          permission: PermissionsEnum.CanBrowseBackofficeTickets,
          feature: TenantFeature.ticketsBackOffice,
        },
        {
          title: 'Translations',
          href: links.ticketsTranslation.url,
          icon: Globe,
          badge: outstandingTickets[TicketsViewType.Translation] || 0,
          permission: PermissionsEnum.CanBrowseTranslationsTickets,
          feature: TenantFeature.ticketsTranslation,
        },
        {
          title: 'Brokerage',
          href: links.ticketsBrokerage.url,
          icon: TrendingUp,
          badge: outstandingTickets[TicketsViewType.Brokerage] || 0,
          permission: PermissionsEnum.CanBrowseBrokerageTickets,
          feature: TenantFeature.ticketsBrokerage,
        },
        {
          title: 'Compliance',
          href: links.ticketsCompliance.url,
          icon: ShieldCheck,
          badge: outstandingTickets[TicketsViewType.Compliance] || 0,
          permission: PermissionsEnum.CanBrowseComplianceTickets,
          feature: TenantFeature.ticketsCompliance,
        },
        {
          title: 'Sales',
          href: links.ticketsSales.url,
          icon: BarChart,
          badge: outstandingTickets[TicketsViewType.Sales] || 0,
          permission: PermissionsEnum.CanBrowseSalesTickets,
          feature: TenantFeature.ticketsSales,
        },
        {
          title: 'My Tickets',
          href: links.ticketsMyTickets.url,
          icon: User,
          badge: myTicketsCount,
          permission: PermissionsEnum.CanViewTicket,
          feature: TenantFeature.ticketsMyTickets,
        },
      ],
    },
    {
      title: 'Transactions',
      icon: Landmark,
      checkPermission: () =>
        hasPermission(permissions, PermissionsEnum.CanBrowseCustomUserWalletTransactions) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseTradingAccountAdjustments) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseCrossWalletTransactions) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseWalletTradingAccountTransactions) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseWalletDepositWithdrawalTransactions) ||
        hasPermission(permissions, PermissionsEnum.CanBrowsePaymentAgentTransactions) ||
        hasPermission(permissions, PermissionsEnum.CanCreateTransactions),
      children: [
        {
          title: 'Adjustments',
          icon: Settings,
          checkPermission: () =>
            hasPermission(permissions, PermissionsEnum.CanBrowseCustomUserWalletTransactions) &&
            hasPermission(permissions, PermissionsEnum.CanBrowseTradingAccountAdjustments),
          children: [
            {
              title: 'Wallets',
              href: links.transactionWalletAdjustments.url,
              icon: Wallet,
              permission: PermissionsEnum.CanBrowseCustomUserWalletTransactions,
              feature: TenantFeature.transactionWalletAdjustments,
            },
            {
              title: 'Trading Accounts',
              href: links.transactionTradingAccountAdjustments.url,
              icon: CreditCard,
              permission: PermissionsEnum.CanBrowseTradingAccountAdjustments,
              feature: TenantFeature.transactionTradingAccountAdjustments,
            },
          ],
        },
        {
          title: 'Wallet Transfers',
          href: links.transactionWallet.url,
          icon: Wallet,
          permission: PermissionsEnum.CanBrowseCrossWalletTransactions,
          feature: TenantFeature.transactionWallet,
        },
        {
          title: 'Platform Transfers',
          href: links.transactionPlatform.url,
          icon: ArrowLeftRight,
          permission: PermissionsEnum.CanBrowseWalletTradingAccountTransactions,
          feature: TenantFeature.transactionPlatform,
        },
        {
          title: 'Deposits/Withdrawals',
          href: links.transactionDepositWithdrawal.url,
          icon: DollarSign,
          permission: PermissionsEnum.CanBrowseWalletDepositWithdrawalTransactions,
          feature: TenantFeature.transactionDepositWithdrawal,
        },
        {
          title: 'IB Transactions',
          icon: UserPlus,
          permission: PermissionsEnum.CanBrowseIBCommissionsTransactions,
          children: [
            {
              title: 'IB Commissions',
              href: links.transactionIBCommissions.url,
              icon: DollarSign,
              permission: PermissionsEnum.CanBrowseIBCommissionsTransactions,
            },
            {
              title: 'IB CashBacks',
              href: links.transactionIBCashBacks.url,
              icon: Gift,
              permission: PermissionsEnum.CanBrowseIBCommissionsTransactions,
            },
            {
              title: 'IB CashBacks Settings',
              href: links.transactionIBCashbackSettings.url,
              icon: Gift,
              permission: PermissionsEnum.CanViewRebatePolicies,
            },
          ],
        },
        {
          title: 'Subscriptions',
          icon: Repeat,
          permission: PermissionsEnum.CanViewSubscriptionRequestCancellations,
          children: [
            {
              title: 'Unsubscribe Requests',
              href: links.transactionUnsubscribeRequests.url,
              icon: FileText,
              permission: PermissionsEnum.CanViewSubscriptionRequestCancellations,
            },
          ],
        },
        {
          title: 'Bonus',
          href: links.transactionBonus.url,
          icon: Gift,
          permission: PermissionsEnum.CanBrowseBonusTransactions,
          feature: TenantFeature.transactionBonus,
        },
        {
          title: 'PA Transfers',
          href: links.transactionPaymentAgent.url,
          icon: Briefcase,
          permission: PermissionsEnum.CanBrowsePaymentAgentTransactions,
          feature: TenantFeature.transactionPaymentAgent,
        },
        {
          title: 'Bulk Transactions',
          icon: Upload,
          permission: PermissionsEnum.CanBrowseBulkTransactionsFileUploadHistory,
          children: [
            {
              title: 'File Upload History',
              href: links.transactionBulkTransaction.url,
              icon: FileUp,
              permission: PermissionsEnum.CanBrowseBulkTransactionsFileUploadHistory,
              feature: TenantFeature.transactionBulkTransaction,
            },
            {
              title: 'Bulk Withdrawals',
              href: links.transactionBulkWithdrawals.url,
              icon: DollarSign,
              permission: PermissionsEnum.CanBrowseBulkWithdrawals,
              feature: TenantFeature.transactionBulkWithdrawals,
            },
          ],
        },
        {
          title: 'Create Transaction',
          icon: PlusCircle,
          permission: PermissionsEnum.CanCreateTransactions,
          children: [
            {
              title: 'CFD',
              href: links.createTransaction.url,
              icon: TrendingUp,
              permission: PermissionsEnum.CanCreateTransactions,
            },
            {
              title: 'ETD',
              href: links.createETDTransaction.url,
              icon: TrendingUp,
              permission: PermissionsEnum.CanCreateTransactions,
            },
          ],
        },
      ],
    },
    {
      title: 'Brokerage',
      icon: ChartColumn,
      checkPermission: () =>
        hasPermission(permissions, PermissionsEnum.CanAddBulkRestrictions) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseDeals) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseIntroducingBrokerProfiles) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseInstruments) ||
        hasPermission(permissions, PermissionsEnum.CanEditDividendPayments) ||
        hasPermission(permissions, PermissionsEnum.CanUpdateMetatrader4Settings) ||
        hasPermission(permissions, PermissionsEnum.CanUpdateMetatrader5Settings) ||
        hasPermission(permissions, PermissionsEnum.CanUpdateServerSessions) ||
        hasPermission(permissions, PermissionsEnum.CanViewTickmillCompanyPlatformTypes),
      children: [
        {
          title: 'Bulk Restrictions',
          href: links.brokerageBulkRestrictions.url,
          icon: ShieldCheck,
          permission: PermissionsEnum.CanAddBulkRestrictions,
          feature: TenantFeature.brokerageBulkRestrictions,
        },
        {
          title: 'Deals',
          href: links.brokerageDeals.url,
          icon: Activity,
          permission: PermissionsEnum.CanViewBrokerageDeals,
          feature: TenantFeature.brokerageDeals,
        },
        {
          title: 'IB Profiles',
          href: links.brokerageIBProfiles.url,
          icon: UserPlus,
          permission: PermissionsEnum.CanBrowseIntroducingBrokerProfiles,
          feature: TenantFeature.brokerageIBProfiles,
        },
        {
          title: 'IB Referrals',
          href: links.brokerageReferralsRestrictions.url,
          icon: Link2,
          permission: PermissionsEnum.CanBrowseTickmillCompanyReferrals,
          feature: TenantFeature.brokerageReferralsRestrictions,
        },
        {
          title: 'Instruments',
          href: links.brokerageInstruments.url,
          icon: BarChart,
          permission: PermissionsEnum.CanBrowseInstruments,
          feature: TenantFeature.brokerageInstruments,
        },
        {
          title: 'Swap Free Rating',
          href: links.brokerageSwapFreeRating.url,
          icon: CirclePercent,
          permission: PermissionsEnum.CanViewBrokerageDeals,
          feature: TenantFeature.brokerageSwapFreeRating,
        },
        {
          title: 'Leverages',
          href: links.brokerageLeverages.url,
          icon: TrendingUp,
          permission: PermissionsEnum.CanBrowseInstruments,
          feature: TenantFeature.brokerageLeverages,
        },
        {
          title: 'Corporate Actions',
          href: links.brokerageDividends.url.replace(':slug', DividendsTab.CustomDividends),
          icon: Building,
          permission: PermissionsEnum.CanEditDividendPayments,
          feature: TenantFeature.brokerageDividends,
        },
        {
          title: 'Platforms',
          icon: Server,
          checkPermission: () =>
            hasPermission(permissions, PermissionsEnum.CanUpdateMetatrader4Settings) ||
            hasPermission(permissions, PermissionsEnum.CanUpdateMetatrader5Settings) ||
            hasPermission(permissions, PermissionsEnum.CanUpdateServerSessions) ||
            hasPermission(permissions, PermissionsEnum.CanViewTickmillCompanyPlatformTypes),
          children: [
            {
              title: 'MT4 Swaps',
              href: links.brokeragePlatformsMT4Swaps.url,
              icon: Server,
              permission: PermissionsEnum.CanUpdateMetatrader4Settings,
            },
            {
              title: 'MT5 Swaps',
              href: links.brokeragePlatformsMT5Swaps.url,
              icon: Server,
              permission: PermissionsEnum.CanUpdateMetatrader5Settings,
            },
            {
              title: 'MT5 Sessions',
              href: links.brokeragePlatformsMT5Sessions.url,
              icon: Server,
              permission: PermissionsEnum.CanUpdateServerSessions,
            },
            {
              title: 'Servers Configuration',
              href: links.brokeragePlatformsConfiguration.url,
              icon: Server,
              permission: PermissionsEnum.CanViewTickmillCompanyPlatformTypes,
            },
          ],
        },
      ],
    },
    {
      title: 'Compliance',
      icon: FileCheckCorner,
      checkPermission: () =>
        hasPermission(permissions, PermissionsEnum.CanViewComplianceCountryRisk) ||
        hasPermission(permissions, PermissionsEnum.CanViewWorldCheckResults) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseAppropriatenessTest) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseLossPercentage),
      children: [
        {
          title: 'Country Risk',
          href: links.complianceCountryRisk.url,
          icon: MapPin,
          permission: PermissionsEnum.CanViewComplianceCountryRisk,
          feature: TenantFeature.complianceCountryRisk,
        },
        {
          title: 'World-Check Cases',
          href: links.complianceWorldCheckCases.url,
          icon: ShieldCheck,
          permission: PermissionsEnum.CanViewWorldCheckResults,
          feature: TenantFeature.complianceWorldCheckCases,
        },
        {
          title: 'Appropriateness Test',
          href: appTestRouteUrl(TestStatusTabs.ACTIVE_TEST_SETS),
          icon: FileCheck,
          permission: PermissionsEnum.CanBrowseAppropriatenessTest,
          feature: TenantFeature.complianceAppropriatenessTests,
        },
        {
          title: 'Disclaimer Loss %',
          href: links.disclaimerLossPercentage.url,
          icon: Percent,
          permission: PermissionsEnum.CanBrowseLossPercentage,
          feature: TenantFeature.disclaimerLossPercentage,
        },
      ],
    },
    {
      title: 'Marketing',
      icon: Megaphone,
      checkPermission: () =>
        hasPermission(permissions, PermissionsEnum.CanBrowseMarketingCampaigns) ||
        hasPermission(permissions, PermissionsEnum.CanGenerateSGLUrl) ||
        hasPermission(permissions, PermissionsEnum.CanViewNotifications) ||
        hasPermission(permissions, PermissionsEnum.CanViewBonusCampaigns) ||
        hasPermission(permissions, PermissionsEnum.CanBrowsePromoMaterials) ||
        hasPermission(permissions, PermissionsEnum.CanViewReferralMaterials),
      children: [
        {
          title: 'Campaigns',
          href: links.marketingCampaigns.url,
          icon: Megaphone,
          permission: PermissionsEnum.CanBrowseMarketingCampaigns,
        },
        {
          title: 'Notifications',
          href: links.marketingNotifications.url,
          icon: Bell,
          permission: PermissionsEnum.CanViewNotifications,
        },
        {
          title: 'Bonuses',
          href: links.bonusModule.url,
          icon: Gift,
          permission: PermissionsEnum.CanViewBonusCampaigns,
        },
        {
          title: 'Rewards Hub',
          href: links.rewardTypeManager.url,
          icon: Trophy,
          permission: PermissionsEnum.CanViewRewards,
        },
        {
          title: 'SGL Link Generator',
          href: links.sglLinkGenerator.url,
          icon: Link2,
          permission: PermissionsEnum.CanGenerateSGLUrl,
        },
        {
          title: 'IB Landing Pages',
          href: links.ibLanding.url,
          icon: FileText,
          permission: PermissionsEnum.CanBrowsePromoMaterials,
        },
        {
          title: 'Referral Materials',
          icon: Image,
          permission: PermissionsEnum.CanViewReferralMaterials,
          children: [
            {
              title: 'RM List',
              href: links.referralMaterials.url,
              icon: FileText,
              permission: PermissionsEnum.CanViewReferralMaterials,
            },
            {
              title: 'Categories',
              href: links.referralMaterialsCategories.url,
              icon: Layers,
              permission: PermissionsEnum.CanViewReferralMaterials,
            },
          ],
        },
        {
          title: 'Emails',
          href: links.emails.url,
          icon: Mail,
        },
      ],
    },
    {
      title: 'Product',
      icon: Blocks,
      permissions: [
        PermissionsEnum.CanBrowseVersions,
        PermissionsEnum.CanEditVersions,
        PermissionsEnum.CanBrowseFeatureFlags,
        PermissionsEnum.CanEditFeatureFlags,
        PermissionsEnum.CanBrowsePlatformVersions,
        PermissionsEnum.CanEditPlatformVersions,
      ],
      children: [
        {
          title: 'Versions',
          href: links.productVersions.url,
          icon: Code,
          permissions: [PermissionsEnum.CanBrowseVersions, PermissionsEnum.CanEditVersions],
        },
        {
          title: 'Feature Flags',
          href: links.productFeatureFlags.url,
          icon: Flag,
          permissions: [PermissionsEnum.CanBrowseFeatureFlags, PermissionsEnum.CanEditFeatureFlags],
        },
        {
          title: 'Platform Versions',
          href: links.productPlatformVersions.url,
          icon: Server,
          permissions: [
            PermissionsEnum.CanBrowsePlatformVersions,
            PermissionsEnum.CanEditPlatformVersions,
          ],
        },
      ],
    },
    {
      title: 'Admin',
      icon: Settings,
      checkPermission: () =>
        hasPermission(permissions, PermissionsEnum.CanViewUsers) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseRoles) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseTeams) ||
        hasPermission(permissions, PermissionsEnum.CanBrowseUserTeams) ||
        hasPermission(permissions, PermissionsEnum.CanViewCountryConfiguration) ||
        hasPermission(permissions, PermissionsEnum.CanBrowsePlatformLinks) ||
        hasPermission(permissions, PermissionsEnum.CanViewPaymentControls),
      children: [
        {
          title: 'Users',
          href: links.administrationUsers.url,
          icon: UserCog,
          permission: PermissionsEnum.CanViewUsers,
          feature: TenantFeature.administrationUsers,
        },
        {
          title: 'User Teams',
          href: links.administrationUserTeams.url,
          icon: Users2,
          permission: PermissionsEnum.CanBrowseUserTeams,
          feature: TenantFeature.administrationUserTeams,
        },
        {
          title: 'Roles',
          href: links.administrationRoles.url,
          icon: ShieldCheck,
          permission: PermissionsEnum.CanBrowseRoles,
          feature: TenantFeature.administrationRoles,
        },
        {
          title: 'Teams',
          href: links.administrationTeams.url,
          icon: Users2,
          permission: PermissionsEnum.CanBrowseTeams,
          feature: TenantFeature.administrationTeams,
        },
        {
          title: 'Countries',
          href: links.administrationCountries.url,
          icon: MapPin,
          permission: PermissionsEnum.CanViewCountryConfiguration,
          feature: TenantFeature.administrationCountries,
        },
        {
          title: 'Subscriptions',
          href: links.administrationSubscriptions.url,
          icon: Repeat,
          permission: PermissionsEnum.CanViewSubscriptions,
          feature: TenantFeature.administrationSubscriptions,
        },
        {
          title: 'Payment Providers',
          icon: CreditCard,
          permission: PermissionsEnum.CanBrowsePaymentProviders,
          feature: TenantFeature.administrationPaymentProvidersConfig,
          children: [
            {
              title: 'Config',
              href: links.administrationPaymentProvidersConfig.url,
              icon: Settings,
              permission: PermissionsEnum.CanBrowsePaymentProviders,
            },
            {
              title: 'Ordering',
              href: links.administrationPaymentProvidersOrdering.url,
              icon: Layers,
              permission: PermissionsEnum.CanBrowsePaymentProviders,
            },
          ],
        },
        {
          title: 'Payment Tools',
          icon: Wrench,
          checkPermission: () =>
            hasPermission(permissions, PermissionsEnum.CanViewPaymentControls) ||
            hasPermission(permissions, PermissionsEnum.CanBrowseInterestRatesConfig) ||
            hasPermission(permissions, PermissionsEnum.CanViewMLWithdrawalAutomationSettings) ||
            hasPermission(permissions, PermissionsEnum.CanViewPaymentProviderThresholds) ||
            isFeatureEnabled(TenantFeature.administrationTransactionProcessingMetrics),
          children: [
            {
              title: 'Payment Controls',
              icon: Settings,
              checkPermission: () => {
                const pspCheckState =
                  isFeatureEnabled(TenantFeature.administrationPaymentControlsPSPCheckState) &&
                  hasPermission(permissions, PermissionsEnum.CanViewPaymentProviderOptions)
                const allowedPsp =
                  isFeatureEnabled(TenantFeature.administrationAllowedPaymentProvidersMatrix) &&
                  hasPermission(permissions, PermissionsEnum.CanBrowsePspGroups)
                return pspCheckState || allowedPsp
              },
              children: [
                {
                  title: 'PSP Check State',
                  href: links.administrationPaymentControlsPSPCheckState.url,
                  icon: FileCheck,
                  permission: PermissionsEnum.CanViewPaymentProviderOptions,
                  feature: TenantFeature.administrationPaymentControlsPSPCheckState,
                },
                {
                  title: "Allowed PSP's",
                  href: links.administrationAllowedPaymentProvidersMatrix.url,
                  icon: ShieldCheck,
                  permission: PermissionsEnum.CanBrowsePspGroups,
                  feature: TenantFeature.administrationAllowedPaymentProvidersMatrix,
                },
              ],
            },
            {
              title: 'Interest Rates',
              href: links.administrationInterestRateConfigControls.url,
              icon: Percent,
              permission: PermissionsEnum.CanBrowseInterestRatesConfig,
              feature: TenantFeature.administrationInterestRateConfigControls,
            },
            {
              title: 'ML Withdrawal Automation',
              href: links.administrationMlWithdrawalAutomationGlobalControls.url,
              icon: Bot,
              permission: PermissionsEnum.CanViewMLWithdrawalAutomationSettings,
              feature: TenantFeature.administrationMlWithdrawalAutomationGlobalControls,
            },
            {
              title: 'Withdrawal Thresholds',
              href: links.administrationWithdrawalThresholds.url,
              icon: Landmark,
              permission: PermissionsEnum.CanViewPaymentProviderThresholds,
              feature: TenantFeature.administrationWithdrawalThresholds,
            },
            {
              title: 'Transaction Processing Metrics',
              href: links.administrationTransactionProcessingMetrics.url,
              icon: BarChart,
              feature: TenantFeature.administrationTransactionProcessingMetrics,
            },
          ],
        },
        {
          title: 'Dev Tools',
          icon: Code,
          permission: PermissionsEnum.CanBrowsePlatformLinks,
          feature: TenantFeature.administrationPlatformLinks,
          children: [
            {
              title: 'Platform Links',
              href: links.administrationPlatformLinks.url,
              icon: Link2,
              permission: PermissionsEnum.CanBrowsePlatformLinks,
            },
            {
              title: 'Contact Details',
              href: links.administrationContactDetails.url,
              icon: Mail,
              permission: PermissionsEnum.CanViewContactDetails,
              feature: TenantFeature.administrationContactDetails,
            },
          ],
        },
        {
          title: 'Rate Markups',
          href: links.administrationRateMarkups.url,
          icon: Percent,
          permission: PermissionsEnum.CanBrowseRateMarkups,
          feature: TenantFeature.administrationRateMarkups,
        },
      ],
    },
  ]

  return navItems
    .map((item) => {
      if (item.children) {
        const filteredChildren = item.children
          .map((child) => {
            if (child.children) {
              const filteredSubChildren = child.children
                .map((subChild) => {
                  if (subChild.children) {
                    const filteredLevel4 = subChild.children.filter((i) =>
                      shouldShowItem(permissions, i)
                    )

                    if (filteredLevel4.length > 0) {
                      return {
                        ...subChild,
                        children: filteredLevel4,
                      }
                    }
                    return shouldShowItem(permissions, subChild) ? subChild : null
                  }
                  return shouldShowItem(permissions, subChild) ? subChild : null
                })
                .filter((subChild) => subChild !== null)

              if (filteredSubChildren.length > 0) {
                return {
                  ...child,
                  children: filteredSubChildren,
                }
              }
              const childVisible = shouldShowItem(permissions, child)
              return childVisible ? child : null
            }
            return shouldShowItem(permissions, child) ? child : null
          })
          .filter((child) => child !== null)

        if (filteredChildren.length > 0) {
          return {
            ...item,
            children: filteredChildren,
          }
        }
        return shouldShowItem(permissions, item) ? item : null
      }
      return shouldShowItem(permissions, item) ? item : null
    })
    .filter((item) => item !== null)
}

const hasRequiredPermission = (permissions: PermissionDto[], item: NavItem): boolean => {
  if (item.checkPermission) {
    return item.checkPermission()
  }
  if (item.permission) {
    return hasPermission(permissions, item.permission)
  }
  if (item.permissions) {
    return hasSomePermissions(permissions, item.permissions)
  }
  return true
}

const hasRequiredFeature = (item: NavItem): boolean => {
  if (!item.feature) return true
  try {
    return isFeatureEnabled(item.feature)
  } catch (e) {
    console.warn('Feature flag check failed:', item.feature, e)
    return true
  }
}

const shouldShowItem = (permissions: PermissionDto[], item: NavItem): boolean => {
  return hasRequiredPermission(permissions, item) && hasRequiredFeature(item)
}
